import { Form, Head } from '@inertiajs/react';
import { LoaderCircle, ShieldCheck } from 'lucide-react';
import InputError from '@/theme2/components/input-error';
import TextLink from '@/theme2/components/text-link';
import { Button } from '@/theme2/components/ui/button';
import { Input } from '@/theme2/components/ui/input';
import { Label } from '@/theme2/components/ui/label';

export default function ForgotPassword({
    status,
    formAction = '/forgot-password',
    loginUrl = '/login',
}: {
    status?: string;
    formAction?: string;
    loginUrl?: string;
}) {
    return (
        <>
            <Head title="Forgot password" />

            <section className="relative isolate overflow-hidden bg-[#f6f0e8] py-10 sm:py-14 lg:py-16">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.95),rgba(246,240,232,0.92)_40%,rgba(232,226,214,0.88)_100%)]" />
                <div className="absolute -top-16 left-1/2 h-56 w-56 -translate-x-1/2 rounded-full bg-[#ead9b4]/35 blur-3xl" />

                <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
                    <div className="mx-auto max-w-2xl overflow-hidden rounded-[2rem] border border-white/80 bg-white/90 shadow-[0_30px_90px_rgba(0,0,0,0.12)] backdrop-blur-2xl">
                        <div className="border-b border-neutral-200/70 px-6 py-6 sm:px-8">
                            <div className="flex items-center justify-between gap-4">
                                <div>
                                    <p className="text-[11px] font-black uppercase tracking-[0.25em] text-neutral-400">Account recovery</p>
                                    <h1 className="mt-3 text-4xl font-black uppercase tracking-[-0.04em] sm:text-5xl">Reset access</h1>
                                    <p className="mt-3 max-w-xl text-sm leading-6 text-neutral-500">
                                        Enter your email and we’ll send a secure link to get you back into your account.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="px-6 py-6 sm:px-8 sm:py-8">
                            <div className="mb-6 flex items-center justify-between rounded-2xl border border-neutral-200 bg-neutral-50 px-4 py-3 text-xs font-semibold text-neutral-500">
                                <span className="inline-flex items-center gap-2">
                                    <ShieldCheck className="size-4" />
                                    Secure password reset
                                </span>
                                <span>Frontend layout</span>
                            </div>

                            {status && (
                                <div className="mb-6 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700">
                                    {status}
                                </div>
                            )}

                            <div className="space-y-6">
                                <Form action={formAction} method="post">
                                    {({ processing, errors }) => (
                                        <>
                                            <div className="grid gap-2">
                                                <Label htmlFor="email">Email address</Label>
                                                <Input
                                                    id="email"
                                                    type="email"
                                                    name="email"
                                                    autoComplete="off"
                                                    autoFocus
                                                    placeholder="email@example.com"
                                                />

                                                <InputError message={errors.email} />
                                            </div>

                                            <div className="my-6 flex items-center justify-start">
                                                <Button
                                                    className="w-full"
                                                    disabled={processing}
                                                    data-test="email-password-reset-link-button"
                                                >
                                                    {processing && (
                                                        <LoaderCircle className="h-4 w-4 animate-spin" />
                                                    )}
                                                    Email password reset link
                                                </Button>
                                            </div>
                                        </>
                                    )}
                                </Form>

                                <div className="space-x-1 text-center text-sm text-muted-foreground">
                                    <span>Or, return to</span>
                                    <TextLink href={loginUrl}>log in</TextLink>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
